import React, { useRef, useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eraser, Undo, Trash2, Pen } from "lucide-react";

export type SketchType =
  | "car"
  | "road"
  | "intersection"
  | "highway"
  | "roundabout"
  | "parking";

interface DamageSketchCanvasProps {
  type: SketchType;
  onTypeChange: (type: SketchType) => void;
  onSketchChange: (dataUrl: string) => void;
  width?: number;
  height?: number;
}

const sketchBackgrounds: Record<
  SketchType,
  {
    label: string;
    draw: (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
    ) => void;
  }
> = {
  car: {
    label: "Bilskiss (ovanifrån)",
    draw: (ctx, width, height) => {
      const centerX = width / 2;
      const centerY = height / 2;
      const carLength = height * 0.75;
      const carWidth = width * 0.35;

      ctx.strokeStyle = "#666";
      ctx.lineWidth = 2;
      ctx.fillStyle = "#f0f0f0";

      const x = centerX - carWidth / 2;
      const y = centerY - carLength / 2;
      const radius = 15;

      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x, y, carWidth, carLength, radius);
      } else {
        ctx.rect(x, y, carWidth, carLength);
      }
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#d0e8ff";
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x + 10, y + 15, carWidth - 20, 30, 5);
      } else {
        ctx.rect(x + 10, y + 15, carWidth - 20, 30);
      }
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x + 10, y + carLength - 45, carWidth - 20, 25, 5);
      } else {
        ctx.rect(x + 10, y + carLength - 45, carWidth - 20, 25);
      }
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#333";
      const wheelWidth = 15;
      const wheelHeight = 25;
      ctx.fillRect(x - 5, y + 40, wheelWidth, wheelHeight);
      ctx.fillRect(x + carWidth - 10, y + 40, wheelWidth, wheelHeight);
      ctx.fillRect(x - 5, y + carLength - 65, wheelWidth, wheelHeight);
      ctx.fillRect(
        x + carWidth - 10,
        y + carLength - 65,
        wheelWidth,
        wheelHeight,
      );

      ctx.fillStyle = "#888";
      ctx.fillRect(x - 12, y + 35, 10, 8);
      ctx.fillRect(x + carWidth + 2, y + 35, 10, 8);

      ctx.fillStyle = "#999";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("FRAM", centerX, y + 8);
      ctx.fillText("BAK", centerX, y + carLength - 5);
      ctx.fillText("V", x - 20, centerY);
      ctx.fillText("H", x + carWidth + 25, centerY);
    },
  },
  road: {
    label: "Väg (rak sträcka)",
    draw: (ctx, width, height) => {
      ctx.fillStyle = "#555";
      ctx.fillRect(width * 0.25, 0, width * 0.5, height);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 3;
      ctx.setLineDash([20, 15]);
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(width * 0.25, 0);
      ctx.lineTo(width * 0.25, height);
      ctx.moveTo(width * 0.75, 0);
      ctx.lineTo(width * 0.75, height);
      ctx.stroke();
      ctx.fillStyle = "#90EE90";
      ctx.fillRect(0, 0, width * 0.25, height);
      ctx.fillRect(width * 0.75, 0, width * 0.25, height);
      ctx.fillStyle = "#fff";
      ctx.font = "24px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("↑", width * 0.375, height * 0.3);
      ctx.fillText("↓", width * 0.625, height * 0.7);
    },
  },
  intersection: {
    label: "Korsning (fyrvägskors)",
    draw: (ctx, width, height) => {
      const roadWidth = width * 0.35;
      const centerX = width / 2;
      const centerY = height / 2;
      ctx.fillStyle = "#90EE90";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#555";
      ctx.fillRect(centerX - roadWidth / 2, 0, roadWidth, height);
      ctx.fillRect(0, centerY - roadWidth / 2, width, roadWidth);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.setLineDash([15, 10]);
      ctx.beginPath();
      ctx.moveTo(centerX, 0);
      ctx.lineTo(centerX, centerY - roadWidth / 2);
      ctx.moveTo(centerX, centerY + roadWidth / 2);
      ctx.lineTo(centerX, height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(centerX - roadWidth / 2, centerY);
      ctx.moveTo(centerX + roadWidth / 2, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(centerX - roadWidth / 2 + 5, centerY - roadWidth / 2 + 5);
      ctx.lineTo(centerX - 5, centerY - roadWidth / 2 + 5);
      ctx.stroke();
    },
  },
  highway: {
    label: "Motorväg (flerfältsväg)",
    draw: (ctx, width, height) => {
      ctx.fillStyle = "#90EE90";
      ctx.fillRect(0, 0, width, height);
      const roadWidth = width * 0.8;
      const roadX = width * 0.1;
      ctx.fillStyle = "#444";
      ctx.fillRect(roadX, 0, roadWidth, height);
      ctx.fillStyle = "#999";
      ctx.fillRect(width / 2 - 5, 0, 10, height);
      const laneWidth = (roadWidth / 2 - 10) / 2;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.setLineDash([20, 15]);
      ctx.beginPath();
      ctx.moveTo(roadX + laneWidth, 0);
      ctx.lineTo(roadX + laneWidth, height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(width - roadX - laneWidth, 0);
      ctx.lineTo(width - roadX - laneWidth, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(roadX, 0);
      ctx.lineTo(roadX, height);
      ctx.moveTo(width - roadX, 0);
      ctx.lineTo(width - roadX, height);
      ctx.stroke();
      ctx.fillStyle = "#fff";
      ctx.font = "20px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("↑", roadX + laneWidth / 2, height * 0.3);
      ctx.fillText("↑", roadX + laneWidth * 1.5, height * 0.5);
      ctx.fillText("↓", width - roadX - laneWidth / 2, height * 0.7);
      ctx.fillText("↓", width - roadX - laneWidth * 1.5, height * 0.5);
    },
  },
  roundabout: {
    label: "Rondell",
    draw: (ctx, width, height) => {
      const centerX = width / 2;
      const centerY = height / 2;
      const outerRadius = Math.min(width, height) * 0.4;
      const innerRadius = outerRadius * 0.5;
      ctx.fillStyle = "#90EE90";
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#555";
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#90EE90";
      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();
      const roadWidth = width * 0.18;
      ctx.fillStyle = "#555";
      ctx.fillRect(
        centerX - roadWidth / 2,
        0,
        roadWidth,
        centerY - outerRadius,
      );
      ctx.fillRect(
        centerX - roadWidth / 2,
        centerY + outerRadius,
        roadWidth,
        height - centerY - outerRadius,
      );
      ctx.fillRect(
        0,
        centerY - roadWidth / 2,
        centerX - outerRadius,
        roadWidth,
      );
      ctx.fillRect(
        centerX + outerRadius,
        centerY - roadWidth / 2,
        width - centerX - outerRadius,
        roadWidth,
      );
      ctx.fillStyle = "#fff";
      ctx.font = "16px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("↻", centerX, centerY);
    },
  },
  parking: {
    label: "Parkeringsplats",
    draw: (ctx, width, height) => {
      ctx.fillStyle = "#666";
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      const spotWidth = width / 5;
      const spotHeight = height * 0.35;
      for (let i = 0; i < 5; i++) {
        ctx.strokeRect(i * spotWidth + 5, 10, spotWidth - 10, spotHeight);
      }
      for (let i = 0; i < 5; i++) {
        ctx.strokeRect(
          i * spotWidth + 5,
          height - spotHeight - 10,
          spotWidth - 10,
          spotHeight,
        );
      }
      ctx.fillStyle = "#fff";
      ctx.font = "24px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("→", width / 2, height / 2);
      ctx.font = "bold 30px sans-serif";
      ctx.fillText("P", width - 30, height / 2);
    },
  },
};

const DamageSketchCanvas: React.FC<DamageSketchCanvasProps> = ({
  type,
  onTypeChange,
  onSketchChange,
  width = 300,
  height = 300,
}) => {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const sk = (d.damageSketch ?? {}) as Record<string, string>;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState("#ff0000");
  const [brushSize, setBrushSize] = useState(3);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [history, setHistory] = useState<ImageData[]>([]);

  const drawBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    sketchBackgrounds[type].draw(ctx, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    setHistory([imageData]);
  }, [type, width, height]);

  useEffect(() => {
    drawBackground();
  }, [drawBackground]);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const imageData = ctx.getImageData(0, 0, width, height);
    setHistory((prev) => [...prev.slice(-10), imageData]);
  };

  const undo = () => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const newHistory = history.slice(0, -1);
    const lastState = newHistory[newHistory.length - 1];
    ctx.putImageData(lastState, 0, 0);
    setHistory(newHistory);
    emitSketch();
  };

  const clear = () => {
    drawBackground();
    emitSketch();
  };

  const emitSketch = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSketchChange(canvas.toDataURL("image/png"));
  };

  const getCoordinates = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { x, y } = getCoordinates(e);
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = color;
    }
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      saveToHistory();
      emitSketch();
    }
    setIsDrawing(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">
            {sk.sketchTypeLabel ?? "Typ av skiss"}
          </Label>
          <Select
            value={type}
            onValueChange={(v) => onTypeChange(v as SketchType)}
          >
            <SelectTrigger className="h-9 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(sketchBackgrounds).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>
                  {(sk[key] ?? label) as string}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant={tool === "pen" ? "default" : "outline"}
          size="sm"
          onClick={() => setTool("pen")}
          className="h-8"
        >
          <Pen className="h-3 w-3 mr-1" />
          {(sk.pen ?? "Penna") as string}
        </Button>
        <Button
          type="button"
          variant={tool === "eraser" ? "default" : "outline"}
          size="sm"
          onClick={() => setTool("eraser")}
          className="h-8"
        >
          <Eraser className="h-3 w-3 mr-1" />
          {(sk.eraser ?? "Sudd") as string}
        </Button>
        <div className="flex items-center gap-1 ml-2">
          <Label className="text-xs">{(sk.color ?? "Färg:") as string}</Label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-7 h-7 rounded cursor-pointer border-0"
          />
          {type !== "car" && (
            <span className="text-xs text-muted-foreground ml-2 flex items-center gap-2">
              <span className="font-medium text-foreground">
                {(sk.redOwnVehicle ?? "Röd = eget fordon") as string}
              </span>
              <span aria-hidden>·</span>
              <span className="font-medium text-foreground">
                {(sk.blueCounterpart ?? "Blå = motpart") as string}
              </span>
            </span>
          )}
        </div>
        {type !== "car" && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setColor("#ff0000")}
              className={`h-8 px-2 border-red-500 text-red-700 hover:bg-red-50 hover:text-red-800 ${color === "#ff0000" ? "bg-red-100 ring-1 ring-red-500" : ""}`}
            >
              {
                (sk.redButton ??
                  sk.redOwnVehicle ??
                  "Röd (eget fordon)") as string
              }
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setColor("#0000ff")}
              className={`h-8 px-2 border-blue-500 text-blue-700 hover:bg-blue-50 hover:text-blue-800 ${color === "#0000ff" ? "bg-blue-100 ring-1 ring-blue-500" : ""}`}
            >
              {
                (sk.blueButton ??
                  sk.blueCounterpart ??
                  "Blå (motpart)") as string
              }
            </Button>
          </div>
        )}
        <div className="flex items-center gap-1 ml-2">
          <Label className="text-xs">{(sk.size ?? "Storlek:") as string}</Label>
          <Select
            value={brushSize.toString()}
            onValueChange={(v) => setBrushSize(parseInt(v))}
          >
            <SelectTrigger className="h-7 w-16">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">{(sk.thin ?? "Tunn") as string}</SelectItem>
              <SelectItem value="4">
                {(sk.normal ?? "Normal") as string}
              </SelectItem>
              <SelectItem value="8">
                {(sk.thick ?? "Tjock") as string}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={undo}
            disabled={history.length <= 1}
            className="h-8"
          >
            <Undo className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            className="h-8 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full cursor-crosshair touch-none"
          style={{ maxHeight: "300px" }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {
          (sk.hintDraw ??
            "Rita på skissen för att markera skadeplats eller händelseförlopp.") as string
        }
        {type !== "car" ? (
          <>
            {" "}
            <strong className="text-foreground">
              {
                (sk.hintSceneColors ??
                  "Använd röd för ditt eget fordon och blå för eventuell motpart.") as string
              }
            </strong>
          </>
        ) : (
          ` ${(sk.hintCarRed ?? "Använd röd färg för att markera skadade områden.") as string}`
        )}
      </p>
    </div>
  );
};

export default DamageSketchCanvas;
